import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Alert } from "@/alerts/types/machine-alert.ts";

/**
 * Tracks how many alerts are "new" since they were last viewed — by diffing the
 * current alert ids against a seen set. Seeds the seen set on the first list so
 * the badge starts at 0, then increments as polling brings new ones in. Only
 * active when `active` (Alert-preview mode); resets when inactive.
 *
 * `markSeen()` clears the count (call it when the user opens the Alerts list). It
 * is referentially stable so it can sit in an effect's deps without looping.
 */
export const useNewAlerts = (alerts: Alert[], active: boolean) => {
  const [seen, setSeen] = useState<Set<number> | null>(null);
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;

  // Functional updates return the same ref when nothing should change, so React
  // bails out — safe even though `alerts` gets a fresh identity each render.
  useEffect(() => {
    if (!active) {
      setSeen((prev) => (prev === null ? prev : null));
      return;
    }
    setSeen((prev) =>
      prev === null && alerts.length > 0 ? new Set(alerts.map((a) => a.id)) : prev,
    );
  }, [active, alerts]);

  const newCount = useMemo(() => {
    if (!active || !seen) return 0;
    return alerts.reduce((n, a) => (seen.has(a.id) ? n : n + 1), 0);
  }, [active, alerts, seen]);

  const markSeen = useCallback(() => {
    setSeen(new Set(alertsRef.current.map((a) => a.id)));
  }, []);

  return { newCount, markSeen };
};
